import { captureReactException } from '@sentry/react';
import { Component, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  STALE_CHUNK_RELOAD_STALL_MS,
  extractRejectionMessage,
  isDynamicImportFailure,
  startStaleChunkRecovery,
  staleChunkRecoveryEnabled,
} from '@/lib/stale-chunk-reload';

interface AppErrorBoundaryFallbackProps {
  readonly error: unknown;
  readonly resetError: () => void;
}

interface AppErrorBoundaryProps {
  readonly children: ReactNode;
  readonly fallback: ComponentType<AppErrorBoundaryFallbackProps>;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: unknown;
  isRecovering: boolean;
}

const INITIAL_STATE: AppErrorBoundaryState = { hasError: false, error: null, isRecovering: false };

/** The hint `Sentry.ErrorBoundary` passes, so these events keep its mechanism. */
const BOUNDARY_CAPTURE_HINT = {
  mechanism: { handled: true, type: 'auto.function.react.error_boundary' },
};

function RecoveryPlaceholder() {
  const { t } = useTranslation('common');
  return (
    <output aria-busy="true" className="block min-h-screen bg-surface">
      <span className="sr-only">{t('states.loading')}</span>
    </output>
  );
}

/**
 * Root error boundary. Reports to Sentry with the hint `Sentry.ErrorBoundary` uses, except for a
 * stale deploy chunk the recovery reload is about to replace: `Sentry.ErrorBoundary` captures
 * every error it catches before the reload runs, so each recovered page still produced an event.
 * Here that capture waits for the page to outlive its reload, the same rule `handleCaughtError`
 * and `TabErrorBoundary` follow; the next boot records the recovery (`stale-chunk-report.ts`).
 */
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  private stalledReloadTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = INITIAL_STATE;
  }

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return {
      hasError: true,
      error,
      isRecovering:
        staleChunkRecoveryEnabled() && isDynamicImportFailure(extractRejectionMessage(error)),
    };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (startStaleChunkRecovery(error)) {
      clearTimeout(this.stalledReloadTimer);
      this.stalledReloadTimer = setTimeout(() => {
        this.setState({ isRecovering: false });
        captureReactException(error, info, BOUNDARY_CAPTURE_HINT);
      }, STALE_CHUNK_RELOAD_STALL_MS);
      return;
    }
    this.setState({ isRecovering: false });
    captureReactException(error, info, BOUNDARY_CAPTURE_HINT);
  }

  componentWillUnmount() {
    clearTimeout(this.stalledReloadTimer);
  }

  private readonly resetError = () => {
    clearTimeout(this.stalledReloadTimer);
    this.setState(INITIAL_STATE);
  };

  render() {
    if (this.state.isRecovering) {
      return <RecoveryPlaceholder />;
    }
    if (this.state.hasError) {
      const Fallback = this.props.fallback;
      return <Fallback error={this.state.error} resetError={this.resetError} />;
    }
    // eslint-disable-next-line react/jsx-no-useless-fragment -- fragment prevents return-await auto-fix from adding async to render()
    return <>{this.props.children}</>;
  }
}
